import { type FormEvent, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api, messageOf, type DocumentResponse } from "../lib/api.ts";
import { useToken } from "../lib/auth.tsx";
import { statusLabel } from "../lib/setup.ts";
import type { AgentOutlet } from "./AgentLayout.tsx";

export function Knowledge() {
  const { agent } = useOutletContext<AgentOutlet>();
  const token = useToken();
  const [documents, setDocuments] = useState<DocumentResponse[] | null>(null);
  const [url, setUrl] = useState("");
  const [showUrl, setShowUrl] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(false);

  async function load(): Promise<void> {
    const body = await api<{ documents: DocumentResponse[] }>(`/v1/agents/${agent.id}/documents`, token);
    setDocuments(body.documents);
  }

  useEffect(() => {
    let active = true;
    void load().catch((caught: unknown) => {
      if (active) setError(messageOf(caught));
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id, token]);

  const waiting = documents?.some((document) => document.status === "queued" || document.status === "processing") ?? false;
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => {
      void load().catch((caught: unknown) => setError(messageOf(caught)));
    }, 3000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, agent.id, token]);

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      for (const file of files) {
        const form = new FormData();
        form.set("file", file);
        await api(`/v1/agents/${agent.id}/documents`, token, { method: "POST", form });
      }
      setNotice(files.length === 1 ? "Added. It will show as ready when the chat can use it." : "Added. They will show as ready when the chat can use them.");
      await load();
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setPending(false);
    }
  }

  async function onUrl(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    setNotice("");
    try {
      await api(`/v1/agents/${agent.id}/documents`, token, { method: "POST", json: { url } });
      setUrl("");
      setNotice("Added. It will show as ready when the chat can use it.");
      await load();
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setPending(false);
    }
  }

  async function remove(documentId: string) {
    setError("");
    try {
      await api(`/v1/agents/${agent.id}/documents/${documentId}`, token, { method: "DELETE" });
      await load();
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  async function retry(documentId: string) {
    setError("");
    try {
      await api(`/v1/agents/${agent.id}/documents/${documentId}/retry`, token, { method: "POST" });
      await load();
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  const ready = documents?.some((document) => document.status === "ready") ?? false;
  const empty = documents?.length === 0;

  return (
    <div className="stack">
      <header className="step-head">
        <p className="eyebrow">Step 2 of 6</p>
        <h1>Add your knowledge</h1>
        <p className="lead">Upload the files a customer would ask about. The chat bubble uses them as soon as they are ready.</p>
      </header>
      {empty ? (
        <section className="card empty-hero">
          <h2>Add your first file</h2>
          <p>A menu, an FAQ, a policy, or a product page. PDF, Word, text, or Markdown, up to 8 MB.</p>
          <label className="button block">
            {pending ? "Adding…" : "Choose a file"}
            <input
              type="file"
              accept=".pdf,.docx,.txt,.md,text/plain,text/markdown,application/pdf"
              multiple
              disabled={pending}
              onChange={(event) => {
                void onFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </label>
        </section>
      ) : (
        <div className="row wrap">
          <label className={ready ? "button ghost" : "button block"}>
            {pending ? "Adding…" : "Add another file"}
            <input
              type="file"
              accept=".pdf,.docx,.txt,.md,text/plain,text/markdown,application/pdf"
              multiple
              disabled={pending}
              onChange={(event) => {
                void onFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </label>
          {ready ? (
            <Link className="button block" to="../answers">
              Next: set how it answers
            </Link>
          ) : null}
        </div>
      )}
      <button className="text-link" type="button" onClick={() => setShowUrl((value) => !value)}>
        {showUrl ? "Hide web page" : "Or add a web page"}
      </button>
      {showUrl ? (
        <form className="card stack" onSubmit={onUrl}>
          <label>
            Page address
            <input type="url" required placeholder="https://example.com/faq" value={url} onChange={(event) => setUrl(event.target.value)} />
          </label>
          <p className="muted">Use a public page. We’ll read the text on it.</p>
          <button className="button ghost" type="submit" disabled={pending}>
            Add page
          </button>
        </form>
      ) : null}
      {error ? <p className="banner bad">{error}</p> : null}
      {notice ? <p className="banner good">{notice}</p> : null}
      {documents === null ? <p className="muted">Loading your files…</p> : null}
      {documents && documents.length > 0 ? (
        <ul className="doc-list">
          {documents.map((document) => (
            <li key={document.id} className="card doc">
              <div>
                <strong>{document.filename ?? document.sourceUrl}</strong>
                <p className="muted">{new Date(document.createdAt).toLocaleString()}</p>
                {document.error ? <p className="banner bad">{document.error}</p> : null}
                {document.note ? <p className="muted">{document.note}</p> : null}
              </div>
              <div className="row">
                <span className={`status ${document.status}`}>{statusLabel(document.status)}</span>
                {document.status === "failed" || document.status === "queued" ? (
                  <button className="button ghost" type="button" onClick={() => void retry(document.id)}>
                    Try again
                  </button>
                ) : null}
                <button className="button ghost" type="button" onClick={() => void remove(document.id)}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
